import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { 
    Sparkles, LogIn, UserPlus, Globe, Sun, Moon, Plane, Apple, 
    Footprints, Brain, Shield, CheckCircle2, Clock, ChevronDown,
    Heart, Zap, Users, ClipboardList, Info, Mail, ArrowLeft, KeyRound
} from 'lucide-react';
import BuddyCheckQuiz from '../components/BuddyCheckQuiz';
import { LandingSections } from '../components/landing/LandingSections';
import { NANOUK, content, apiErrorMessage } from './authContent';
import { LiveDemoCarousel } from '../components/LiveDemoCarousel';
import { LanguageChoiceBar } from '../components/LanguageChoiceBar';
import { Seo } from '../components/seo/Seo';
import { FaqSection } from '../components/seo/FaqSection';
import { FAQS } from '../components/seo/faqData';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;


export default function Auth() {
    const { login, register } = useAuth();
    const { language, setLanguage } = useLanguage();
    const t = content[language] || content.de;
    const navigate = useNavigate();
    const location = useLocation();

    // Toggle language + keep the URL in sync (/ ↔ /en) so English has its own URL
    const handleLangToggle = () => {
        const next = language === 'de' ? 'en' : 'de';
        try { localStorage.setItem('bestbuddy_lang_chosen', next); } catch (_e) { /* ignore */ }
        if (location.pathname === '/' || location.pathname === '/en') {
            navigate(next === 'en' ? '/en' : '/', { replace: true, state: { lang: next } });
        } else {
            setLanguage(next);
        }
    };
    const [loading, setLoading] = useState(false);
    const [showAuth, setShowAuth] = useState(false);
    const [showQuiz, setShowQuiz] = useState(false);
    const [showForgotPassword, setShowForgotPassword] = useState(false);
    const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
    const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
    const [forgotPasswordSent, setForgotPasswordSent] = useState(false);
    const [quizAnswers, setQuizAnswers] = useState(null);
    const [authMode, setAuthMode] = useState('login');
    const [loginForm, setLoginForm] = useState({ email: '', password: '' });
    const [registerForm, setRegisterForm] = useState({ name: '', email: '', password: '', confirmPassword: '' });

    // Single entry-point so every CTA explicitly picks login vs register.
    // Prevents the recurring UX bug where users opened the modal in register
    // mode and tried to log in there.
    const openAuth = (mode) => {
        setAuthMode(mode);
        setShowAuth(true);
    };

    const handleQuizComplete = (answers) => {
        setQuizAnswers(answers);
        // Persist for P2 first-day plan bottomsheet that fires on the first
        // dashboard visit after registration. Cleared once shown.
        try {
            localStorage.setItem('bestbuddy_quiz_answers', JSON.stringify(answers));
            localStorage.setItem('bestbuddy_first_day_plan_pending', '1');
        } catch (_e) { /* localStorage disabled — silently skip */ }
        setShowQuiz(false);
        openAuth('register');
        toast.success(language === 'de' ? 'Super! Jetzt registriere dich!' : 'Great! Now register!');
    };

    const handleLogin = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await login(loginForm.email, loginForm.password);
            toast.success(language === 'de' ? 'Willkommen zurück, Buddy!' : 'Welcome back, Buddy!');
        } catch (error) {
            toast.error(apiErrorMessage(error, language === 'de' ? 'Login fehlgeschlagen' : 'Login failed'));
        } finally {
            setLoading(false);
        }
    };

    const handleRegister = async (e) => {
        e.preventDefault();
        if (registerForm.password !== registerForm.confirmPassword) {
            toast.error(language === 'de' ? 'Passwörter stimmen nicht überein' : 'Passwords do not match');
            return;
        }
        if (registerForm.password.length < 6) {
            toast.error(language === 'de' ? 'Passwort muss mindestens 6 Zeichen haben' : 'Password must be at least 6 characters');
            return;
        }
        setLoading(true);
        try {
            await register(registerForm.email, registerForm.password, registerForm.name);
        } catch (error) {
            toast.error(apiErrorMessage(error, language === 'de' ? 'Registrierung fehlgeschlagen' : 'Registration failed'));
        } finally {
            setLoading(false);
        }
    };

    const scrollToSection = (id) => {
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    };

    const handleForgotPassword = async (e) => {
        e.preventDefault();
        setForgotPasswordLoading(true);
        try {
            await axios.post(`${API}/auth/forgot-password`, { email: forgotPasswordEmail });
            setForgotPasswordSent(true);
            toast.success(language === 'de' 
                ? 'Reset-Link gesendet! Prüfe deine E-Mails.' 
                : 'Reset link sent! Check your email.');
        } catch (error) {
            // Still show success to prevent email enumeration
            setForgotPasswordSent(true);
        } finally {
            setForgotPasswordLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-background text-foreground overflow-x-hidden" data-testid="auth-page">
            <Seo
                title={language === 'de' ? 'Kostenloser KI-Wellness-Begleiter mit Husky Nanouk' : 'Free AI Wellness Companion with Husky Nanouk'}
                description={language === 'de'
                    ? 'Tracke Ernährung, Schlaf, Fitness & Gesundheit in einer kostenlosen App. KI-Foto-Scan, evidenzbasierte Analysen und Husky Nanouk als Coach.'
                    : 'Track nutrition, sleep, fitness & health in one free app. AI photo scan, evidence-based insights and husky Nanouk as your coach.'}
                path={language === 'en' ? '/en' : '/'}
                language={language}
                withHreflang
            />
            {/* Language Toggle - Fixed */}
            <div className="fixed top-4 right-4 z-50">
                <Button
                    variant="outline"
                    size="sm"
                    onClick={handleLangToggle}
                    className="gap-2 bg-white/10 backdrop-blur border-white/20 text-foreground hover:bg-white/20"
                    data-testid="auth-language-toggle"
                >
                    <Globe className="w-4 h-4" />
                    {language === 'de' ? '🇬🇧 EN' : '🇩🇪 DE'}
                </Button>
            </div>

            {/* ==================== HERO SECTION - NANOUK ABOVE THE FOLD ==================== */}
            <section className="min-h-screen flex flex-col items-center justify-center relative px-4 py-20">
                {/* Background Effects */}
                <div className="absolute inset-0 overflow-hidden">
                    <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/20 rounded-full blur-3xl" />
                    <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl" />
                </div>

                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    className="text-center relative z-10 max-w-3xl mx-auto"
                >
                    {/* Nanouk Image - Prominent */}
                    <motion.div
                        initial={{ scale: 0.8, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ delay: 0.2, type: 'spring', bounce: 0.4 }}
                        className="relative inline-block mb-8"
                    >
                        <div className="w-40 h-40 md:w-56 md:h-56 rounded-full overflow-hidden border-4 border-cyan-400/50 shadow-2xl shadow-primary/30">
                            <img 
                                src={NANOUK.happy}
                                alt="Nanouk"
                                className="w-full h-full object-cover"
                            />
                        </div>
                        {/* Speech Bubble */}
                        <motion.div
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.8 }}
                            className="absolute -right-4 md:-right-16 top-0 bg-white text-slate-900 px-4 py-2 rounded-2xl rounded-bl-none shadow-lg max-w-[200px]"
                        >
                            <p className="text-sm font-medium">Wuff! 🐺</p>
                        </motion.div>
                    </motion.div>

                    {/* Greeting Text */}
                    <motion.h1
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.4 }}
                        className="font-display text-4xl md:text-6xl font-bold mb-4 bg-gradient-to-r from-primary via-primary to-primary bg-clip-text text-transparent"
                    >
                        {t.heroGreeting}
                    </motion.h1>

                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.5 }}
                        className="text-xl md:text-2xl text-muted-foreground mb-2"
                    >
                        {t.heroSubtitle}
                    </motion.p>

                    {/* Target audience chips */}
                    <motion.div
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.55 }}
                        className="flex flex-wrap items-center justify-center gap-2 mt-3 mb-6"
                        data-testid="audience-chips"
                    >
                        <span className="text-xs uppercase tracking-wider text-muted-foreground/80 font-semibold">
                            {t.audienceLabel}
                        </span>
                        {t.audienceChips.map((chip, i) => (
                            <Badge
                                key={i}
                                variant="outline"
                                className="text-xs font-medium gap-1 border-cyan-500/30 bg-cyan-500/5 text-foreground hover:bg-cyan-500/10"
                                data-testid={`audience-chip-${i}`}
                            >
                                <span aria-hidden="true">{chip.emoji}</span>
                                {chip.label}
                            </Badge>
                        ))}
                    </motion.div>

                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.6 }}
                        className="text-lg text-primary font-medium mb-4"
                    >
                        {t.heroQuestion}
                    </motion.p>

                    {/* "2 min/day" promise badge */}
                    <motion.div
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.65 }}
                        className="flex justify-center mb-8"
                        data-testid="two-min-badge"
                    >
                        <span className="inline-flex items-center gap-2 rounded-full border border-amber-400/40 bg-amber-400/10 px-4 py-1.5 text-sm font-semibold text-amber-600 dark:text-amber-300">
                            <Clock className="w-4 h-4" />
                            {t.twoMinBadge}
                        </span>
                    </motion.div>

                    {/* CTA Button */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.7 }}
                        className="flex flex-col sm:flex-row gap-4 justify-center items-center"
                    >
                        <Button
                            size="lg"
                            onClick={() => setShowQuiz(true)}
                            className="text-lg px-8 py-6 bg-primary text-primary-foreground hover:bg-primary/90 rounded-full shadow-lg shadow-primary/30 gap-2"
                            data-testid="hero-cta-btn"
                        >
                            <Brain className="w-5 h-5" />
                            {t.quizCta}
                        </Button>
                        <Button
                            variant="outline"
                            size="lg"
                            onClick={() => openAuth('register')}
                            className="border-cyan-500/50 text-primary hover:bg-primary/10"
                            data-testid="direct-register-btn"
                        >
                            <Heart className="w-5 h-5 mr-2" />
                            {t.heroCta}
                        </Button>
                    </motion.div>
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.9 }}
                        className="mt-4 flex flex-col sm:flex-row gap-3 justify-center items-center"
                    >
                        <Button
                            variant="ghost"
                            onClick={() => openAuth('login')}
                            className="text-muted-foreground hover:text-foreground"
                            data-testid="hero-login-btn"
                        >
                            {t.alreadyBuddies} <LogIn className="w-4 h-4 ml-2" />
                        </Button>
                        <Button
                            variant="ghost"
                            onClick={() => navigate('/meet-nanouk')}
                            className="text-primary/70 hover:text-cyan-300 hover:bg-primary/10"
                        >
                            <Info className="w-4 h-4 mr-2" />
                            {t.meetNanouk}
                        </Button>
                    </motion.div>
                </motion.div>

                {/* Scroll Indicator */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 1.2 }}
                    className="absolute bottom-8 left-1/2 -translate-x-1/2 cursor-pointer"
                    onClick={() => scrollToSection('day-section')}
                >
                    <p className="text-sm text-muted-foreground mb-2">{t.scrollDown}</p>
                    <ChevronDown className="w-6 h-6 text-primary mx-auto animate-bounce" />
                </motion.div>
            </section>

            <LandingSections t={t} language={language} openAuth={openAuth} />

            {/* FAQ (visible accordion + FAQPage JSON-LD) */}
            <section className="py-16 px-4 bg-secondary/30" data-testid="landing-faq">
                <FaqSection faqs={FAQS.landing[language]} language={language} />
            </section>

            {/* Footer with legal + language links */}
            <footer className="py-10 px-4 border-t border-border" data-testid="landing-footer">
                <div className="max-w-4xl mx-auto flex flex-col items-center gap-4 text-sm text-muted-foreground">
                    <nav className="flex flex-wrap justify-center gap-x-6 gap-y-2">
                        <a href={language === 'en' ? '/en/about' : '/about'} className="hover:text-foreground transition-colors" data-testid="footer-about-link">
                            {language === 'de' ? 'Über BESTBUDDY' : 'About'}
                        </a>
                        <a href={language === 'en' ? '/en/blog' : '/blog'} className="hover:text-foreground transition-colors" data-testid="footer-blog-link">
                            Blog
                        </a>
                        <a href={language === 'en' ? '/en/meet-nanouk' : '/meet-nanouk'} className="hover:text-foreground transition-colors" data-testid="footer-nanouk-link">
                            {language === 'de' ? 'Nanouk kennenlernen' : 'Meet Nanouk'}
                        </a>
                        <a href={language === 'en' ? '/en/terms' : '/terms'} className="hover:text-foreground transition-colors" data-testid="footer-terms-link">
                            {language === 'de' ? 'Nutzungsbedingungen' : 'Terms of Service'}
                        </a>
                        <a href={language === 'en' ? '/en/legal' : '/legal'} className="hover:text-foreground transition-colors" data-testid="footer-legal-link">
                            {language === 'de' ? 'Impressum & Datenschutz' : 'Legal & Privacy'}
                        </a>
                    </nav>
                    <div className="flex items-center gap-3" data-testid="footer-language-links">
                        <a href="/" onClick={(e) => { e.preventDefault(); try { localStorage.setItem('bestbuddy_lang_chosen', 'de'); } catch (_e) {} navigate('/', { replace: true, state: { lang: 'de' } }); }} className={`hover:text-foreground transition-colors ${language === 'de' ? 'font-semibold text-foreground' : ''}`}>🇩🇪 Deutsch</a>
                        <span aria-hidden="true">·</span>
                        <a href="/en" onClick={(e) => { e.preventDefault(); try { localStorage.setItem('bestbuddy_lang_chosen', 'en'); } catch (_e) {} navigate('/en', { replace: true, state: { lang: 'en' } }); }} className={`hover:text-foreground transition-colors ${language === 'en' ? 'font-semibold text-foreground' : ''}`}>🇬🇧 English</a>
                    </div>
                    <p className="text-xs">© {new Date().getFullYear()} MYSYMP AG · Nottwil, {language === 'de' ? 'Schweiz' : 'Switzerland'}</p>
                </div>
            </footer>

            {/* ==================== AUTH MODAL ==================== */}
            {showAuth && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
                    onClick={(e) => e.target === e.currentTarget && setShowAuth(false)}
                >
                    <motion.div
                        initial={{ scale: 0.9, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        className="w-full max-w-md"
                    >
                        <Card className="bg-card border-border shadow-2xl" data-testid="auth-modal">
                            <Tabs value={authMode} onValueChange={setAuthMode}>
                                <CardHeader className="pb-3">
                                    <div className="flex items-center justify-center gap-3 mb-5">
                                        <div className="w-12 h-12 rounded-xl overflow-hidden">
                                            <img src={NANOUK.happy} alt="Nanouk" className="w-full h-full object-cover" />
                                        </div>
                                        <div>
                                            <h2 className="font-bold text-xl text-foreground">BESTBUDDY</h2>
                                            <p className="text-xs text-muted-foreground">Track. Travel. Triumph.</p>
                                        </div>
                                    </div>
                                    <p className="text-center text-sm font-medium text-muted-foreground mb-2">
                                        {t.tabHint}
                                    </p>
                                    <TabsList className="w-full h-auto p-1.5 bg-muted/70 border border-border rounded-xl grid grid-cols-2 gap-1">
                                        <TabsTrigger
                                            value="login"
                                            className="flex flex-col items-center gap-0.5 py-3 px-2 rounded-lg font-semibold transition-all data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-md data-[state=inactive]:text-muted-foreground"
                                            data-testid="auth-tab-login"
                                        >
                                            <div className="flex items-center gap-2">
                                                <LogIn className="w-4 h-4" />
                                                <span className="text-base">{t.tabLogin}</span>
                                            </div>
                                            <span className="text-[10px] font-normal opacity-80">{t.alreadyBuddies}</span>
                                        </TabsTrigger>
                                        <TabsTrigger
                                            value="register"
                                            className="flex flex-col items-center gap-0.5 py-3 px-2 rounded-lg font-semibold transition-all data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-md data-[state=inactive]:text-muted-foreground"
                                            data-testid="auth-tab-register"
                                        >
                                            <div className="flex items-center gap-2">
                                                <UserPlus className="w-4 h-4" />
                                                <span className="text-base">{t.tabRegister}</span>
                                            </div>
                                            <span className="text-[10px] font-normal opacity-80">{t.newBuddy}</span>
                                        </TabsTrigger>
                                    </TabsList>
                                </CardHeader>

                                <CardContent className="pt-4">
                                    <TabsContent value="login" className="mt-0">
                                        <form onSubmit={handleLogin} className="space-y-4">
                                            <div className="space-y-2">
                                                <Label htmlFor="login-email" className="text-muted-foreground">{t.email}</Label>
                                                <Input
                                                    id="login-email"
                                                    type="email"
                                                    value={loginForm.email}
                                                    onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                                                    placeholder="buddy@example.com"
                                                    required
                                                    className="bg-muted/50 border-border text-foreground"
                                                    data-testid="login-email-input"
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <Label htmlFor="login-password" className="text-muted-foreground">{t.password}</Label>
                                                <Input
                                                    id="login-password"
                                                    type="password"
                                                    value={loginForm.password}
                                                    onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                                                    placeholder="••••••••"
                                                    required
                                                    className="bg-muted/50 border-border text-foreground"
                                                    data-testid="login-password-input"
                                                />
                                            </div>
                                            <Button 
                                                type="submit" 
                                                className="w-full bg-primary text-primary-foreground hover:bg-primary/90" 
                                                disabled={loading}
                                                data-testid="login-submit-btn"
                                            >
                                                {loading ? t.loading : t.login}
                                            </Button>
                                            
                                            {/* Forgot Password Link - prominently placed */}
                                            <button
                                                type="button"
                                                onClick={() => setShowForgotPassword(true)}
                                                className="w-full text-center text-sm text-primary hover:text-cyan-300 hover:underline py-2"
                                                data-testid="forgot-password-link"
                                            >
                                                {language === 'de' ? '🔑 Passwort vergessen?' : '🔑 Forgot password?'}
                                            </button>
                                        </form>
                                    </TabsContent>

                                    <TabsContent value="register" className="mt-0">
                                        <form onSubmit={handleRegister} className="space-y-4">
                                            <div className="space-y-2">
                                                <Label htmlFor="register-name" className="text-muted-foreground">{t.name}</Label>
                                                <Input
                                                    id="register-name"
                                                    type="text"
                                                    value={registerForm.name}
                                                    onChange={(e) => setRegisterForm({ ...registerForm, name: e.target.value })}
                                                    placeholder="Max Mustermann"
                                                    required
                                                    className="bg-muted/50 border-border text-foreground"
                                                    data-testid="register-name-input"
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <Label htmlFor="register-email" className="text-muted-foreground">{t.email}</Label>
                                                <Input
                                                    id="register-email"
                                                    type="email"
                                                    value={registerForm.email}
                                                    onChange={(e) => setRegisterForm({ ...registerForm, email: e.target.value })}
                                                    placeholder="buddy@example.com"
                                                    required
                                                    className="bg-muted/50 border-border text-foreground"
                                                    data-testid="register-email-input"
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <Label htmlFor="register-password" className="text-muted-foreground">{t.password}</Label>
                                                <Input
                                                    id="register-password"
                                                    type="password"
                                                    value={registerForm.password}
                                                    onChange={(e) => setRegisterForm({ ...registerForm, password: e.target.value })}
                                                    placeholder="Min. 6 Zeichen"
                                                    required
                                                    className="bg-muted/50 border-border text-foreground"
                                                    data-testid="register-password-input"
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <Label htmlFor="register-confirm" className="text-muted-foreground">{t.confirmPassword}</Label>
                                                <Input
                                                    id="register-confirm"
                                                    type="password"
                                                    value={registerForm.confirmPassword}
                                                    onChange={(e) => setRegisterForm({ ...registerForm, confirmPassword: e.target.value })}
                                                    placeholder="Passwort wiederholen"
                                                    required
                                                    className="bg-muted/50 border-border text-foreground"
                                                    data-testid="register-confirm-input"
                                                />
                                            </div>
                                            <Button 
                                                type="submit" 
                                                className="w-full bg-primary text-primary-foreground hover:bg-primary/90" 
                                                disabled={loading}
                                                data-testid="register-submit-btn"
                                            >
                                                {loading ? t.loading : t.heroCta}
                                            </Button>
                                        </form>
                                    </TabsContent>

                                    <p className="text-center text-xs text-muted-foreground mt-4">
                                        {t.privacyNotice}
                                    </p>
                                </CardContent>
                            </Tabs>
                        </Card>
                    </motion.div>
                </motion.div>
            )}

            {/* Buddy-Check Quiz Modal */}
            {showQuiz && (
                <BuddyCheckQuiz
                    language={language}
                    onComplete={handleQuizComplete}
                    onClose={() => setShowQuiz(false)}
                />
            )}

            {/* Forgot Password Modal */}
            {showForgotPassword && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
                    onClick={(e) => e.target === e.currentTarget && setShowForgotPassword(false)}
                >
                    <motion.div
                        initial={{ scale: 0.9, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        className="w-full max-w-md"
                    >
                        <Card className="bg-secondary border-border shadow-2xl">
                            <CardHeader className="pb-2">
                                <div className="flex items-center justify-center gap-3 mb-4">
                                    <div className="w-12 h-12 rounded-xl overflow-hidden">
                                        <img src={NANOUK.smart} alt="Nanouk" className="w-full h-full object-cover" />
                                    </div>
                                    <div>
                                        <h2 className="font-bold text-xl text-foreground">
                                            {language === 'de' ? 'Passwort vergessen?' : 'Forgot Password?'}
                                        </h2>
                                        <p className="text-xs text-muted-foreground">
                                            {language === 'de' ? 'Kein Problem, Buddy!' : 'No worries, Buddy!'}
                                        </p>
                                    </div>
                                </div>
                            </CardHeader>

                            <CardContent className="pt-4">
                                {!forgotPasswordSent ? (
                                    <form onSubmit={handleForgotPassword} className="space-y-4">
                                        <p className="text-sm text-muted-foreground text-center">
                                            {language === 'de' 
                                                ? 'Gib deine E-Mail-Adresse ein und wir senden dir einen Link zum Zurücksetzen deines Passworts.'
                                                : 'Enter your email address and we\'ll send you a link to reset your password.'}
                                        </p>
                                        <div className="space-y-2">
                                            <Label htmlFor="forgot-email" className="text-muted-foreground">
                                                {language === 'de' ? 'E-Mail' : 'Email'}
                                            </Label>
                                            <Input
                                                id="forgot-email"
                                                type="email"
                                                value={forgotPasswordEmail}
                                                onChange={(e) => setForgotPasswordEmail(e.target.value)}
                                                placeholder="buddy@example.com"
                                                required
                                                className="bg-muted/50 border-border text-foreground"
                                                data-testid="forgot-email-input"
                                            />
                                        </div>
                                        <div className="flex gap-3">
                                            <Button
                                                type="button"
                                                variant="outline"
                                                className="flex-1 border-border text-muted-foreground hover:bg-secondary"
                                                onClick={() => setShowForgotPassword(false)}
                                            >
                                                <ArrowLeft className="w-4 h-4 mr-2" />
                                                {language === 'de' ? 'Zurück' : 'Back'}
                                            </Button>
                                            <Button
                                                type="submit"
                                                className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90"
                                                disabled={forgotPasswordLoading}
                                                data-testid="forgot-submit-btn"
                                            >
                                                {forgotPasswordLoading ? (
                                                    <span>{language === 'de' ? 'Senden...' : 'Sending...'}</span>
                                                ) : (
                                                    <>
                                                        <Mail className="w-4 h-4 mr-2" />
                                                        {language === 'de' ? 'Link senden' : 'Send Link'}
                                                    </>
                                                )}
                                            </Button>
                                        </div>
                                    </form>
                                ) : (
                                    <div className="text-center space-y-4">
                                        <div className="w-16 h-16 mx-auto bg-green-500/20 rounded-full flex items-center justify-center">
                                            <Mail className="w-8 h-8 text-green-500" />
                                        </div>
                                        <h3 className="text-lg font-semibold text-foreground">
                                            {language === 'de' ? 'E-Mail gesendet!' : 'Email Sent!'}
                                        </h3>
                                        <p className="text-sm text-muted-foreground">
                                            {language === 'de'
                                                ? 'Wenn ein Konto mit dieser E-Mail existiert, erhältst du in Kürze einen Link zum Zurücksetzen deines Passworts.'
                                                : 'If an account with this email exists, you\'ll receive a password reset link shortly.'}
                                        </p>
                                        <p className="text-xs text-muted-foreground">
                                            {language === 'de'
                                                ? '⏰ Der Link ist 1 Stunde gültig.'
                                                : '⏰ The link is valid for 1 hour.'}
                                        </p>
                                        <Button
                                            onClick={() => {
                                                setShowForgotPassword(false);
                                                setForgotPasswordSent(false);
                                                setForgotPasswordEmail('');
                                            }}
                                            className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
                                        >
                                            {language === 'de' ? 'Zurück zum Login' : 'Back to Login'}
                                        </Button>
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </motion.div>
                </motion.div>
            )}
            {/* First-visit language choice bar */}
            <LanguageChoiceBar />
        </div>
    );
}
